import {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useState,
  type ReactNode,
} from 'react';

export interface ConsentState {
  essential: boolean;
  analytics: boolean;
  marketing: boolean;
}

interface ConsentContextValue extends ConsentState {
  decided: boolean;
  setConsent: (next: Partial<ConsentState>) => void;
  acceptAll: () => void;
  rejectAll: () => void;
  reset: () => void;
}

const STORAGE_KEY = 'cookie_consent';

const DEFAULT_STATE: ConsentState = {
  essential: true,
  analytics: false,
  marketing: false,
};

const ConsentContext = createContext<ConsentContextValue | null>(null);

function readStored(): { state: ConsentState; decided: boolean } {
  try {
    const raw = localStorage.getItem(STORAGE_KEY);
    if (!raw) return { state: DEFAULT_STATE, decided: false };
    const parsed = JSON.parse(raw) as Partial<ConsentState>;
    return {
      state: {
        essential: true,
        analytics: Boolean(parsed.analytics),
        marketing: Boolean(parsed.marketing),
      },
      decided: true,
    };
  } catch {
    return { state: DEFAULT_STATE, decided: false };
  }
}

function persist(state: ConsentState): void {
  try {
    localStorage.setItem(STORAGE_KEY, JSON.stringify(state));
  } catch {
    void 0;
  }
}

export function ConsentProvider({ children }: { children: ReactNode }) {
  const [{ state, decided }, setInner] = useState(() => readStored());

  useEffect(() => {
    if (decided) persist(state);
  }, [state, decided]);

  const setConsent = useCallback((next: Partial<ConsentState>) => {
    setInner((prev) => ({
      state: { ...prev.state, ...next, essential: true },
      decided: true,
    }));
  }, []);

  const acceptAll = useCallback(() => {
    setInner({ state: { essential: true, analytics: true, marketing: true }, decided: true });
  }, []);

  const rejectAll = useCallback(() => {
    setInner({ state: { essential: true, analytics: false, marketing: false }, decided: true });
  }, []);

  const reset = useCallback(() => {
    try {
      localStorage.removeItem(STORAGE_KEY);
    } catch {
      void 0;
    }
    setInner({ state: DEFAULT_STATE, decided: false });
  }, []);

  const value = useMemo<ConsentContextValue>(
    () => ({ ...state, decided, setConsent, acceptAll, rejectAll, reset }),
    [state, decided, setConsent, acceptAll, rejectAll, reset],
  );

  return <ConsentContext.Provider value={value}>{children}</ConsentContext.Provider>;
}

export function useConsent(): ConsentContextValue {
  const ctx = useContext(ConsentContext);
  if (!ctx) {
    throw new Error('useConsent must be used inside a <ConsentProvider>');
  }
  return ctx;
}
